import React, { ChangeEvent, FocusEvent, KeyboardEvent, ReactNode } from 'react';
import {
  Box,
  FormControl,
  FormHelperText,
  OutlinedInput,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';

export interface TextAreaProps {
  label?: string;
  name: string;
  value?: string;
  onChange?: (event: ChangeEvent<HTMLTextAreaElement>) => void;
  onBlur?: (event: FocusEvent<HTMLTextAreaElement>) => void;
  onFocus?: (event: FocusEvent<HTMLTextAreaElement>) => void;
  onKeyDown?: (event: KeyboardEvent<HTMLTextAreaElement>) => void;
  disabled?: boolean;
  customClass?: string;
  className?: string;
  rows?: number;
  minRows?: number;
  maxRows?: number;
  placeholder?: string;
  required?: boolean;
  error?: boolean;
  helperText?: ReactNode;
  variant?: 'default' | 'pill';
  fullWidth?: boolean;
  id?: string;
  sx?: SxProps<Theme>;
}

export const TextArea = ({
  label,
  name,
  value = '',
  onChange,
  onBlur,
  onFocus,
  onKeyDown,
  disabled = false,
  customClass,
  className,
  rows = 3,
  minRows,
  maxRows,
  placeholder = '',
  required = false,
  error = false,
  helperText,
  variant = 'default',
  fullWidth = true,
  id,
  sx,
}: TextAreaProps) => {
  const isPill = variant === 'pill';
  const inputId = id || name;

  return (
    <FormControl
      fullWidth={fullWidth}
      error={Boolean(error)}
      disabled={disabled}
      className={className || customClass}
      sx={{
        width: fullWidth ? '100%' : 'auto',
        ...sx,
      }}
    >
      {label && (
        <Typography
          component="label"
          htmlFor={inputId}
          sx={{
            display: 'block',
            mb: 0.75,
            fontSize: '0.75rem',
            fontWeight: 600,
            color: error ? 'error.main' : 'text.primary',
            letterSpacing: '0.01em',
            fontFamily: 'inherit',
          }}
        >
          {label}
          {required && (
            <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>
              *
            </Box>
          )}
        </Typography>
      )}

      <OutlinedInput
        id={inputId}
        name={name}
        multiline
        rows={rows}
        minRows={minRows}
        maxRows={maxRows}
        value={value}
        placeholder={placeholder}
        onChange={onChange}
        onBlur={onBlur}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        disabled={disabled}
        required={required}
        sx={{
          borderRadius: isPill ? '16px' : '12px',
          fontFamily: 'inherit',
          transition: 'all 200ms ease',
          backgroundColor: isPill ? 'rgba(244, 244, 245, 0.85)' : '#ffffff',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#e4e4e7',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#d4d4d8',
          },
          ...(disabled && {
            backgroundColor: '#f4f4f5',
            cursor: 'not-allowed',
          }),
        }}
      />

      {helperText && (
        <FormHelperText
          sx={{
            ml: 0.5,
            mt: 0.5,
            fontSize: '0.72rem',
            fontFamily: 'inherit',
            color: error ? 'error.main' : 'text.secondary',
          }}
        >
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
};
